import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { LienCvARattacher } from './LienCvARattacher';

const LIENS_ADMINISTRATION: ReadonlyArray<[string, string]> = [
  ['/administration/comptes', 'Comptes et rôles'],
  ['/administration/referentiels', 'Points de vente et métiers'],
  ['/administration/baremes', 'Barèmes'],
  ['/administration/journal', 'Journal'],
  ['/parametres', 'Paramètres'],
];

/**
 * Menu principal déroulant, ouvert par le bouton à trois lignes en haut à
 * gauche. Il se referme au choix d'une rubrique, à la touche Échap (le focus
 * revient au bouton) et au clic en dehors. Fermé, il reste monté mais masqué :
 * le compteur « CV à rattacher » continue d'être relu à chaque changement d'écran.
 */
export function MenuPrincipal({
  rh,
  superAdmin,
  onDeconnexion,
}: {
  rh: boolean;
  superAdmin: boolean;
  onDeconnexion: () => void;
}) {
  const [ouvert, setOuvert] = useState(false);
  const { pathname } = useLocation();
  const bouton = useRef<HTMLButtonElement>(null);
  const panneau = useRef<HTMLElement>(null);
  const pageAdmin = pathname.startsWith('/administration') || pathname === '/parametres';

  useEffect(() => {
    setOuvert(false);
  }, [pathname]);

  useEffect(() => {
    if (!ouvert) return;
    const clavier = (evenement: KeyboardEvent) => {
      if (evenement.key !== 'Escape') return;
      setOuvert(false);
      bouton.current?.focus();
    };
    const pointeur = (evenement: PointerEvent) => {
      const cible = evenement.target as Node;
      if (!panneau.current?.contains(cible) && !bouton.current?.contains(cible)) setOuvert(false);
    };
    document.addEventListener('keydown', clavier);
    document.addEventListener('pointerdown', pointeur);
    return () => {
      document.removeEventListener('keydown', clavier);
      document.removeEventListener('pointerdown', pointeur);
    };
  }, [ouvert]);

  /** Un lien vers la page déjà affichée ne change pas l'adresse : le menu se ferme quand même. */
  function fermerSurLien(evenement: MouseEvent<HTMLElement>): void {
    if ((evenement.target as HTMLElement).closest('a')) setOuvert(false);
  }

  return (
    <>
      <button
        ref={bouton}
        type="button"
        className="bouton-menu"
        aria-expanded={ouvert}
        aria-controls="menu-principal"
        aria-label={ouvert ? 'Fermer le menu' : 'Ouvrir le menu'}
        onClick={() => setOuvert((v) => !v)}
      >
        <span className="trois-lignes" aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
      </button>
      <nav
        id="menu-principal"
        ref={panneau}
        className="menu-principal"
        aria-label="Menu principal"
        hidden={!ouvert}
        onClick={fermerSurLien}
      >
        <NavLink to="/annonces" end>
          Annonces
        </NavLink>
        {rh && <NavLink to="/recherche">Recherche</NavLink>}
        <NavLink to="/suivi">Suivi</NavLink>
        <NavLink to="/employes">Employés</NavLink>
        <NavLink to="/statistiques">Statistiques</NavLink>
        {rh && <LienCvARattacher />}
        {rh && <NavLink to="/conservation">Conservation</NavLink>}
        {rh && <NavLink to="/ingestion">Ingestion</NavLink>}
        {superAdmin && (
          <>
            {/* Actif sur toutes les pages d'administration, Paramètres compris. */}
            <Link
              to="/administration/comptes"
              className={pageAdmin ? 'active' : undefined}
              aria-current={pageAdmin ? 'page' : undefined}
            >
              Administration
            </Link>
            <div className="sous-menu">
              {LIENS_ADMINISTRATION.map(([vers, libelle]) => (
                <NavLink key={vers} to={vers}>
                  {libelle}
                </NavLink>
              ))}
            </div>
          </>
        )}
        <div className="menu-compte">
          <NavLink to="/mon-compte">Mon compte</NavLink>
          <button
            type="button"
            className="lien"
            onClick={() => {
              setOuvert(false);
              onDeconnexion();
            }}
          >
            Se déconnecter
          </button>
        </div>
      </nav>
    </>
  );
}
